import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DownloadResourcesSection = () => {
  const downloadItems = [
    {
      id: 'enterprise-guide',
      title: 'Enterprise Implementation Guide',
      description: 'Complete guide to deploying PolicyAlerts across your organization with best practices, integration patterns, and team onboarding strategies.',
      icon: 'BookOpen',
      iconColor: '#1A73E8',
      iconBg: '#1A73E8',
      fileType: 'PDF',
      fileSize: '4.2 MB',
      pages: '48 pages',
      lastUpdated: 'January 2026',
      downloadCount: '2,847',
      features: [
        'Step-by-step deployment roadmap',
        'Integration architecture patterns',
        'Team training & onboarding',
        'Change management strategies',
        'Success metrics & KPIs'
      ]
    },
    {
      id: 'security-pack',
      title: 'Security & Compliance Pack',
      description: 'Comprehensive security documentation including SOC 2 reports, penetration test results, HIPAA compliance attestations, and data protection certifications.',
      icon: 'ShieldCheck',
      iconColor: '#10B981',
      iconBg: '#10B981',
      fileType: 'ZIP',
      fileSize: '12.8 MB',
      pages: '6 documents',
      lastUpdated: 'December 2025',
      downloadCount: '1,523',
      features: [
        'SOC 2 Type II audit report',
        'Penetration test results',
        'HIPAA compliance documentation',
        'ISO 27001 certification',
        'Security architecture whitepaper'
      ]
    },
    {
      id: 'roi-calculator',
      title: 'ROI Analysis Toolkit',
      description: 'Interactive spreadsheet and methodology guide to calculate your organization\'s return on investment with PolicyAlerts, including cost-benefit analysis templates.',
      icon: 'Calculator',
      iconColor: '#0066FF',
      iconBg: '#0066FF',
      fileType: 'XLSX',
      fileSize: '1.8 MB',
      pages: '12 worksheets',
      lastUpdated: 'January 2026',
      downloadCount: '3,192',
      features: [
        'Cost savings calculator',
        'Time efficiency analysis',
        'Risk mitigation valuation',
        'Productivity impact metrics',
        'Executive summary templates'
      ]
    },
    {
      id: 'api-integration',
      title: 'API Integration Playbook',
      description: 'Technical guide for developers covering API authentication, webhook configuration, data models, and sample code for common integration scenarios.',
      icon: 'Code',
      iconColor: '#ED1C24',
      iconBg: '#ED1C24',
      fileType: 'PDF',
      fileSize: '3.5 MB',
      pages: '64 pages',
      lastUpdated: 'December 2025',
      downloadCount: '1,876',
      features: [
        'Complete API reference',
        'Authentication & security',
        'Webhook implementation guide',
        'Sample code in 5 languages',
        'Troubleshooting & best practices'
      ]
    }
  ];

  const handleDownload = (item) => {
    // Mock download functionality - in production this would trigger actual file download
    console.log(`Downloading: ${item?.title}`);
    // Simulate download
    alert(`Download started: ${item?.title} (${item?.fileSize})`);
  };

  return (
    <section className="bg-white py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#1A73E8]/10 border border-[#1A73E8]/20 rounded-full px-4 py-2 mb-4">
            <Icon name="Download" size={16} color="#1A73E8" />
            <span className="text-xs md:text-sm font-medium text-[#1A73E8]">Enterprise Resources</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B1B20] mb-4">
            Download Enterprise Resources
          </h2>
          <p className="text-base md:text-lg text-[#6B7280] max-w-3xl mx-auto">
            Access comprehensive guides, security documentation, and implementation toolkits to accelerate your PolicyAlerts deployment
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {downloadItems?.map((item) => (
            <div
              key={item?.id}
              className="bg-[#FAFBFC] border border-[#E5E7EB] rounded-2xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 hover:border-[#1A73E8]/30"
            >
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${item?.iconBg}20` }}
                >
                  <Icon name={item?.icon} size={32} color={item?.iconColor} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-[#1B1B20] mb-2">
                    {item?.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-full px-3 py-1 text-xs font-medium text-[#6B7280]">
                      <Icon name="FileText" size={12} color="#6B7280" />
                      {item?.fileType}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-full px-3 py-1 text-xs font-medium text-[#6B7280]">
                      <Icon name="HardDrive" size={12} color="#6B7280" />
                      {item?.fileSize}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-full px-3 py-1 text-xs font-medium text-[#6B7280]">
                      <Icon name="Layers" size={12} color="#6B7280" />
                      {item?.pages}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-sm md:text-base text-[#6B7280] mb-6 leading-relaxed">
                {item?.description}
              </p>

              <div className="bg-white rounded-xl p-4 mb-6 border border-[#E5E7EB]">
                <h4 className="text-sm font-semibold text-[#1B1B20] mb-3 flex items-center gap-2">
                  <Icon name="CheckCircle2" size={16} color="#10B981" />
                  What's Included
                </h4>
                <ul className="space-y-2">
                  {item?.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#6B7280]">
                      <Icon name="Check" size={16} color="#10B981" className="flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} color="#6B7280" />
                    Updated {item?.lastUpdated}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Download" size={12} color="#6B7280" />
                    {item?.downloadCount} downloads
                  </span>
                </div>
              </div>

              <Button
                variant="default"
                size="lg"
                iconName="Download"
                iconPosition="left"
                onClick={() => handleDownload(item)}
                className="w-full bg-[#1A73E8] hover:bg-[#0066FF] text-white"
              >
                Download {item?.fileType}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-r from-[#1A73E8]/10 to-[#0066FF]/10 border border-[#1A73E8]/20 rounded-2xl p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1B1B20] mb-3">
                Need Custom Resources?
              </h3>
              <p className="text-sm md:text-base text-[#6B7280] mb-4">
                Our enterprise team can provide customized documentation, training materials, and implementation guides tailored to your organization's specific needs.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="default"
                iconName="Mail"
                iconPosition="left"
                className="bg-[#1A73E8] hover:bg-[#0066FF] text-white"
              >
                Contact Enterprise Team
              </Button>
              <Button
                variant="outline"
                iconName="Calendar"
                iconPosition="left"
                className="border-[#1A73E8] text-[#1A73E8] hover:bg-[#1A73E8]/10"
              >
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DownloadResourcesSection;